import { CreditRequests, type CreditRequest } from "./credit-requests";
import { Alert, Stack, Text } from "@softmax/design-system";
import {
  breakpoints,
  fonts,
  fontSizes,
  spacing,
} from "@softmax/design-system/tokens.stylex";
import * as stylex from "@stylexjs/stylex";
import {
  AccountReadStatus,
  type AccountReadState,
} from "../account-read-status";

export type AccountCreditsData = {
  readonly balance: string;
  readonly balanceDescription: string;
  readonly refill: { readonly amount: string; readonly detail: string };
  readonly used: string;
  readonly conversion: string;
  readonly notice: string | null;
  readonly requests: readonly CreditRequest[];
};

export function AccountCredits({
  state,
  onRetry,
}: {
  readonly state: AccountReadState<AccountCreditsData>;
  readonly onRetry: () => void;
}) {
  return (
    <Stack width="auto">
      <div {...stylex.props(styles.header)}>
        <Text as="p" size="small" tone="muted">
          <svg
            {...stylex.props(styles.infoIcon)}
            viewBox="0 0 16 16"
            width="16"
            height="16"
            aria-hidden="true"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.25"
          >
            <circle cx="8" cy="8" r="7" />
            <path d="M8 7.25v4" strokeLinecap="round" />
            <circle cx="8" cy="4.9" r="0.6" fill="currentColor" stroke="none" />
          </svg>
          Credits pay for experience requests. League play does not use them.
        </Text>
      </div>
      {state.status !== "ready" ? (
        <AccountReadStatus state={state} onRetry={onRetry} />
      ) : (
        <>
          {state.refresh ? (
            <AccountReadStatus state={state.refresh} onRetry={onRetry} />
          ) : null}
          {state.data.notice ? <Alert>{state.data.notice}</Alert> : null}
          <div {...stylex.props(styles.metrics)}>
            <div>
              <Text as="span" size="label">
                Available credits
              </Text>
              <strong {...stylex.props(styles.value)}>
                {state.data.balance}
              </strong>
              <Text size="small" tone="muted">
                {state.data.balanceDescription}
              </Text>
            </div>
            <div>
              <Text as="span" size="label">
                Free refill
              </Text>
              <strong {...stylex.props(styles.value)}>
                {state.data.refill.amount}
              </strong>
              <Text size="small" tone="muted">
                {state.data.refill.detail}
              </Text>
            </div>
            <div>
              <Text as="span" size="label">
                Used this period
              </Text>
              <strong {...stylex.props(styles.value)}>{state.data.used}</strong>
              <Text size="small" tone="muted">
                {state.data.conversion}
              </Text>
            </div>
          </div>
          {state.data.requests.length > 0 ? (
            <CreditRequests rows={state.data.requests} />
          ) : (
            <Text as="p" size="small" tone="muted">
              No recent experience requests.
            </Text>
          )}
        </>
      )}
    </Stack>
  );
}

const styles = stylex.create({
  infoIcon: {
    display: "inline-block",
    verticalAlign: "-3px",
    marginRight: spacing.space6,
    flexShrink: 0,
  },
  header: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "space-between",
    alignItems: "center",
    gap: spacing.space16,
  },
  metrics: {
    display: "grid",
    gridTemplateColumns: {
      default: "repeat(3, minmax(0, 1fr))",
      [breakpoints.compact]: "1fr",
    },
    gap: spacing.space24,
  },
  value: {
    display: "block",
    fontFamily: fonts.sans,
    fontSize: fontSizes.pageTitle,
    marginBlock: spacing.space4,
  },
});
